<script setup lang="tsx">
import i18n from '@/i18n'
import jiraCbStore from '@/stores/jira-checkboxes'
import {
	type Action,
	StartDelete,
	StartExport,
	StartSync,
	StartImport,
	ImportInProgress,
	DeleteInProgress,
	ExportInProgress,
} from '@/models/jira-enums-generated'
import { computed } from 'vue'
import { hasPermissionOwn } from '@/services/utils'
import { PermAllJiraDelete, PermAllJiraExport, PermOwnJiraDelete, PermOwnJiraExport } from '@/models/enums-generated'

const jiraCb = jiraCbStore()
const MAX_SELECTION_COUNT = 50

const inProgress = computed(
	() => jiraCb.jiraStatus === ImportInProgress || jiraCb.jiraStatus === DeleteInProgress || jiraCb.jiraStatus === ExportInProgress,
)

const isLimitExceeded = computed(() => jiraCb.selectedCheckboxes.length > MAX_SELECTION_COUNT)
const hasEmptyJiraURL = computed(() => jiraCb.selectedCheckboxes.some((cb) => cb.jiraUrl === ''))

const isExportDisabled = computed(
	() =>
		!jiraCb.selectedCheckboxes.length ||
		isLimitExceeded.value ||
		inProgress.value ||
		jiraCb.isSomethingWrong ||
		!jiraCb.selectedCheckboxes.every((cb) => hasPermissionOwn(cb.responsible, PermAllJiraExport, PermOwnJiraExport)),
)
const isDeleteDisabled = computed(
	() =>
		!jiraCb.selectedCheckboxes.length ||
		isLimitExceeded.value ||
		hasEmptyJiraURL.value ||
		inProgress.value ||
		jiraCb.isSomethingWrong ||
		!jiraCb.selectedCheckboxes.every((cb) => hasPermissionOwn(cb.responsible, PermAllJiraDelete, PermOwnJiraDelete)),
)
const isSyncDisabled = computed(() => inProgress.value)

const selectedCountText = computed(() => `${jiraCb.selectedCheckboxes.length} / ${MAX_SELECTION_COUNT}`)

const closeJiraMode = () => {
	jiraCb.isJiraMode = false
}

const setValues = (action: Action) => {
	jiraCb.action = action
	if (action !== StartSync && action !== StartImport) {
		jiraCb.isShowModal = true
	}
}

const deleteTooltip = computed(() => {
	if (jiraCb.jiraStatus === ImportInProgress) return i18n.global.t('jira.importProgress')
	if (isLimitExceeded.value) return i18n.global.t('jira.limitExceededDelete', { count: MAX_SELECTION_COUNT })
	if (hasEmptyJiraURL.value) return i18n.global.t('jira.methodNotExported')
	if (!jiraCb.selectedCheckboxes.every((cb) => hasPermissionOwn(cb.responsible, PermAllJiraDelete, PermOwnJiraDelete)))
		return i18n.global.t('jira.permissionMissingDelete')
	return null
})

const exportTooltip = computed(() => {
	if (jiraCb.jiraStatus === ImportInProgress) return i18n.global.t('jira.importProgress')
	if (isLimitExceeded.value) return i18n.global.t('jira.limitExceededExport', { count: MAX_SELECTION_COUNT })
	if (!jiraCb.selectedCheckboxes.every((cb) => hasPermissionOwn(cb.responsible, PermAllJiraExport, PermOwnJiraExport)))
		return i18n.global.t('jira.permissionMissingExport')
	return null
})

const importTooltip = computed(() => (jiraCb.jiraStatus === ImportInProgress ? i18n.global.t('jira.importProgress') : null))
</script>

<template>
	<div v-if="jiraCb.isJiraMode" class="container fixed-bottom mb-4">
		<div class="row justify-content-center">
			<div class="col-auto text-center">
				<div class="d-inline-flex justify-content-between toolbar text-nowrap">
					<button class="btn btn-close me-3 mt-2" @click="closeJiraMode" :aria-label="$t('misc.cancel')" role="button"></button>
					<div v-one-tooltip="importTooltip">
						<button
							class="btn btn-outline-secondary me-3"
							@click="setValues(StartSync)"
							:disabled="isSyncDisabled"
							:aria-label="$t('jira.sync')"
							role="button"
						>
							<i class="bi bi-arrow-repeat"></i>
							{{ $t('jira.sync') }}
						</button>
					</div>
					<div v-one-tooltip="deleteTooltip">
						<button
							class="btn btn-outline-danger me-3"
							:disabled="isDeleteDisabled"
							@click="setValues(StartDelete)"
							:aria-label="$t('misc.delete')"
							role="button"
						>
							<i class="bi bi-trash"></i>
							{{ $t('misc.delete') }}: {{ selectedCountText }}
						</button>
					</div>
					<div v-one-tooltip="importTooltip">
						<button
							class="btn btn-outline-success me-3"
							@click="setValues(StartImport)"
							:disabled="isSyncDisabled"
							:aria-label="$t('jira.import')"
							role="button"
						>
							<i class="bi bi-box-arrow-down"></i>
							{{ $t('jira.import') }}
						</button>
					</div>
					<div v-one-tooltip="exportTooltip">
						<button
							class="btn btn-outline-primary"
							:disabled="isExportDisabled"
							@click="setValues(StartExport)"
							:aria-label="$t('jira.export')"
							role="button"
						>
							<i class="bi bi-box-arrow-in-up"></i>
							{{ $t('jira.export') }}: {{ selectedCountText }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
